import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fetchJSON } from '../../utils/api';
import { formatDateTime } from '../../utils/dateFormat';
import { fmtDuration, fmtPercent, QuizVerdictBadge, QUESTION_TYPE_LABELS } from './quizFormat';

/**
 * One person's answer sheet, question by question — opened from the session
 * detail panel's Quiz tab (ld:view). Shows what was picked against the key;
 * the paper is read in the order this person saw it. Opens on the latest
 * sitting; when there were several, a row of attempt buttons switches.
 */
const QuizAttemptModal = ({ sessionId, staffId, name, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [attemptNo, setAttemptNo] = useState(null);   // null = latest

  useEffect(() => {
    let cancelled = false;
    const q = attemptNo == null ? '' : `?attempt=${attemptNo}`;
    fetchJSON(`/api/ld/sessions/${sessionId}/quiz-attempts/${staffId}${q}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [sessionId, staffId, attemptNo]);

  const a = data?.attempt;
  const attempts = data?.attempts || [];

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 truncate">Quiz Answers — {name}</h3>
            {a && (
              <p className="text-sm text-slate-500">
                {attempts.length > 1 && <span className="font-medium text-slate-700">Attempt {a.attemptNo} of {attempts.length} · </span>}
                {a.submittedAt
                  ? <>Score {a.score} / {a.maxScore} ({fmtPercent(a.percent)}) · {fmtDuration(a.durationSeconds)} · submitted {formatDateTime(a.submittedAt)}</>
                  : a.expiredAt
                    ? <>Expired, not graded — started {formatDateTime(a.startedAt)}, expired {formatDateTime(a.expiredAt)}</>
                    : <>In progress — started {formatDateTime(a.startedAt)}</>}
                {a.violations > 0 && (
                  <span className="ml-2 text-amber-700">focus lost ×{a.violations}</span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {a && a.submittedAt && <QuizVerdictBadge passed={a.passed} />}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          {!error && !data && <p className="text-sm text-slate-500">Loading…</p>}
          {/* Several sittings: one button each, verdict in the label. */}
          {attempts.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              {attempts.map((x) => {
                const on = a && x.attemptNo === a.attemptNo;
                return (
                  <button
                    key={x.attemptNo}
                    type="button"
                    onClick={() => setAttemptNo(x.attemptNo)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${
                      on ? 'bg-blue-50 border-blue-400 text-blue-800' : 'bg-white border-slate-300 text-slate-600 hover:border-blue-300'}`}
                  >
                    Attempt {x.attemptNo}
                    <span className="text-slate-300">·</span>
                    {x.submittedAt
                      ? <span className="tabular-nums text-slate-500">{fmtPercent(x.percent)}</span>
                      : <span className="text-slate-400">{x.expiredAt ? 'expired' : 'open'}</span>}
                    {x.submittedAt && x.passed != null && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className={x.passed ? 'text-emerald-700' : 'text-amber-700'}>{x.passed ? 'Passed' : 'Not passed'}</span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {data && data.questions.map((q, i) => (
            <div key={q.id} className={`rounded-lg border p-4 ${q.isCorrect ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-sm font-medium text-slate-900">
                  <span className="text-slate-500 mr-1.5">{i + 1}.</span>{q.text}
                </p>
                <span className={`shrink-0 text-xs font-semibold ${q.isCorrect ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {q.isCorrect ? `+${q.points}` : '0'} / {q.points}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                {QUESTION_TYPE_LABELS[q.type]}
                {q.mustPass && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-semibold">Must answer correctly</span>
                )}
              </p>
              <ul className="space-y-1">
                {q.options.map((o) => {
                  const picked = q.picked.includes(o.key);
                  const key = q.correct.includes(o.key);
                  return (
                    <li key={o.key} className={`flex items-start gap-2 text-sm px-2 py-1 rounded ${picked ? 'bg-white border border-slate-200' : ''}`}>
                      <span className={`shrink-0 w-5 text-xs font-semibold uppercase ${key ? 'text-emerald-700' : 'text-slate-400'}`}>{o.key}</span>
                      <span className={`flex-1 ${key ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>{o.text}</span>
                      {picked && <span className="shrink-0 text-xs text-slate-500">picked</span>}
                      {key && <span className="shrink-0 text-xs text-emerald-700">correct</span>}
                    </li>
                  );
                })}
              </ul>
              {q.picked.length === 0 && <p className="text-xs text-slate-500 mt-1">Not answered.</p>}
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizAttemptModal;
